import type { RefObject } from "react";
import { useCallback, useRef, useState } from "react";

interface UseCopyStyledOptions {
	/** Duration in milliseconds before the copied state resets */
	timeout?: number;
	/** Whether to include HTML formatting in the clipboard */
	includeFormatting?: boolean;
}

interface UseCopyStyledReturn {
	/** Function to trigger the copy operation */
	copyToClipboard: () => Promise<boolean>;
	/** Whether the content was successfully copied */
	copied: boolean;
	/** Ref to attach to the element containing the content to copy */
	targetRef: RefObject<HTMLDivElement>;
}

export const useCopyStyled = (options: UseCopyStyledOptions = {}): UseCopyStyledReturn => {
	const { timeout = 2000, includeFormatting = true } = options;
	const [copied, setCopied] = useState(false);
	const targetRef = useRef<HTMLDivElement>(null);
	const timeoutRef = useRef<NodeJS.Timeout>();

	const copyToClipboard = useCallback(async (): Promise<boolean> => {
		if (!targetRef.current) return false;

		try {
			const element = targetRef.current;
			const plainText = element.innerText;

			if (includeFormatting) {
				const htmlContent = element.innerHTML;
				const clipboardData = new ClipboardItem({
					'text/html': new Blob([htmlContent], { type: 'text/html' }),
					'text/plain': new Blob([plainText], { type: 'text/plain' })
				});
				await navigator.clipboard.write([clipboardData]);
			} else {
				await navigator.clipboard.writeText(plainText);
			}

			setCopied(true);

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			timeoutRef.current = setTimeout(() => {
				setCopied(false);
			}, timeout);

			return true;
		} catch (err) {
			console.error('Copy failed:', err);
			return false;
		}
	}, [timeout, includeFormatting]);

	return { copyToClipboard, copied, targetRef };
};